import React from "react";
import { useTranslation } from "react-i18next";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import {
  KILL_SWITCH_MODULES,
  KillSwitchModule,
} from "@/types/module-kill-switch";

export interface AuditFilterValues {
  module: KillSwitchModule | "";
  userId: string;
  dateFrom: Date | null;
  dateTo: Date | null;
}

interface AuditFiltersProps {
  value: AuditFilterValues;
  onChange: (next: AuditFilterValues) => void;
}

const ALL_MODULES_VALUE = "__all__";

export const AuditFilters: React.FC<AuditFiltersProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();

  const patch = (partial: Partial<AuditFilterValues>) =>
    onChange({ ...value, ...partial });

  const hasAnyFilter =
    value.module !== "" ||
    value.userId.trim() !== "" ||
    value.dateFrom !== null ||
    value.dateTo !== null;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <FilterField label={t("modules.audit.filters.module")}>
          <Select
            value={value.module || ALL_MODULES_VALUE}
            onValueChange={(next) =>
              patch({
                module:
                  next === ALL_MODULES_VALUE ? "" : (next as KillSwitchModule),
              })
            }
          >
            <SelectTrigger size="sm" className="w-full h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_MODULES_VALUE}>
                {t("modules.audit.filters.allModules")}
              </SelectItem>
              {KILL_SWITCH_MODULES.map((m) => (
                <SelectItem key={m} value={m}>
                  {t(`modules.codes.${m}.label`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label={t("modules.audit.filters.user")}>
          <Input
            value={value.userId}
            onChange={(event) => patch({ userId: event.target.value })}
            placeholder={t("modules.audit.filters.userPlaceholder")}
            className="h-10"
          />
        </FilterField>

        <FilterField label={t("modules.audit.filters.dateFrom")}>
          <DateFilterButton
            date={value.dateFrom}
            maxDate={value.dateTo ?? undefined}
            onChange={(date) => patch({ dateFrom: date })}
          />
        </FilterField>

        <FilterField label={t("modules.audit.filters.dateTo")}>
          <DateFilterButton
            date={value.dateTo}
            minDate={value.dateFrom ?? undefined}
            onChange={(date) => patch({ dateTo: date })}
          />
        </FilterField>
      </div>

      {hasAnyFilter && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="new_ghost"
            size="sm"
            onClick={() =>
              onChange({
                module: "",
                userId: "",
                dateFrom: null,
                dateTo: null,
              })
            }
          >
            <X className="size-3.5" />
            {t("modules.audit.filters.reset")}
          </Button>
        </div>
      )}
    </div>
  );
};

interface FilterFieldProps {
  label: string;
  children: React.ReactNode;
}

const FilterField: React.FC<FilterFieldProps> = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </Label>
    {children}
  </div>
);

interface DateFilterButtonProps {
  date: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
}

const DateFilterButton: React.FC<DateFilterButtonProps> = ({
  date,
  onChange,
  minDate,
  maxDate,
}) => {
  const [open, setOpen] = React.useState(false);

  return (
    <div className="flex items-center gap-2 w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="new_outline"
            className={cn(
              "w-full justify-start gap-2 h-10 font-normal",
              !date && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="size-4" />
            {date ? format(date, "MMM d, yyyy") : "—"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date ?? undefined}
            onSelect={(next) => {
              onChange(next ?? null);
              setOpen(false);
            }}
            disabled={[
              ...(minDate ? [{ before: minDate }] : []),
              ...(maxDate ? [{ after: maxDate }] : []),
            ]}
          />
        </PopoverContent>
      </Popover>
      {date && (
        <Button
          type="button"
          variant="ghost"
          size="icon_sm"
          onClick={() => onChange(null)}
          aria-label="Clear date"
        >
          <X className="size-3.5" />
        </Button>
      )}
    </div>
  );
};
